import type { PropsWithChildren } from "react"
import { forwardRef, useCallback, useEffect, useRef, useState } from "react"

import { Box, Stack, Typography } from "@mui/material"
import type { EmblaCarouselType } from "embla-carousel"
import useEmblaCarousel from "embla-carousel-react"

import type { FC, FCWC, iconKeys } from ".."
import { Container, SvgIcon } from ".."

type PerkItemProps = {
  title: string
  description: string
  icon: string
}

type PerksListProps = {
  title: string
  subtitle: string
  items: PerkItemProps[]
}

const PerksList: FC<PerksListProps> = ({ title, subtitle, items }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollTrackRef = useRef<HTMLDivElement>(null)
  const scrollThumbRef = useRef<HTMLDivElement>(null)
  const [thumbWidth, setThumbWidth] = useState(20)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    breakpoints: {
      "(min-width: 768px)": {
        active: false,
      },
    },
  })

  const onInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const snaps = emblaApi.scrollSnapList()
      setScrollSnaps(snaps)

      if (containerRef.current && scrollTrackRef.current) {
        const { clientWidth, scrollWidth } = containerRef.current
        const { clientWidth: trackSize } = scrollTrackRef.current
        setThumbWidth(Math.max((clientWidth / scrollWidth) * trackSize, 20))
      }
    },
    [containerRef, scrollTrackRef]
  )

  const onScroll = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!scrollTrackRef.current || !scrollThumbRef.current) return

      const progress = emblaApi.scrollProgress()
      const { clientWidth: trackWidth } = scrollTrackRef.current
      const scrollX = progress * (trackWidth - thumbWidth)
      const thumb = scrollThumbRef.current
      thumb.style.left = `${scrollX}px`
    },
    [thumbWidth]
  )

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const { current: trackCurrent } = scrollTrackRef
      const { current: contentCurrent } = containerRef

      if (!emblaApi || !trackCurrent || !contentCurrent) return

      const { clientX } = e
      const target = e.target as HTMLDivElement
      const rect = target.getBoundingClientRect()
      const trackLeft = rect.left
      const clickRatio = (clientX - trackLeft) / trackCurrent.clientWidth

      const closestSlide = scrollSnaps.reduce((prev, curr) => {
        return Math.abs(curr - clickRatio) < Math.abs(prev - clickRatio) ? curr : prev
      }, 0)
      const closestIndex = scrollSnaps.indexOf(closestSlide)
      emblaApi?.scrollTo(closestIndex)
    },
    [scrollSnaps, scrollTrackRef, containerRef]
  )

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    emblaApi.on("reInit", onInit)
    emblaApi.on("resize", onInit)
    emblaApi.on("scroll", onScroll)
  }, [emblaApi, containerRef, scrollTrackRef, onInit, onScroll])
  return (
    <PerksListSection>
      <Box display="flex" flexDirection="column" gap={{ xs: 5, md: 10 }} maxWidth={771}>
        <PerksListSubtitle subtitle={subtitle} />
        <PerksListTitle title={title} />
      </Box>
      <Box>
        <Box
          ref={emblaRef}
          sx={(theme) => ({
            [theme.breakpoints.down("md")]: {
              "cursor": "grab",

              "&:active": {
                cursor: "grabbing",
              },
            },
          })}
        >
          <PerkItems ref={containerRef}>
            {items.map((item, i) => (
              <PerkItem key={i} {...item} />
            ))}
          </PerkItems>
          <Box
            width="100%"
            height="2px"
            mt={15}
            position="relative"
            sx={(theme) => ({
              [theme.breakpoints.up("md")]: {
                display: "none",
              },
            })}
          >
            <Box
              ref={scrollTrackRef}
              onClick={handleTrackClick}
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bgcolor={(theme) => theme.palette["neutral.200"].main}
              sx={{ "&:hover": { cursor: "pointer" } }}
            />
            <Box
              ref={scrollThumbRef}
              position="absolute"
              width={thumbWidth}
              top={0}
              left={0}
              bottom={0}
              bgcolor={(theme) => theme.palette["secondary.800"].main}
            />
          </Box>
        </Box>
      </Box>
    </PerksListSection>
  )
}

export default PerksList

/** A sub-component for PerkItems */
const PerkItems = forwardRef(({ children }: PropsWithChildren, ref) => (
  <Box
    ref={ref}
    display="grid"
    rowGap={{ md: 25 }}
    columnGap={10}
    justifyContent="space-between"
    gridAutoFlow={{ xs: "column", md: "row" }}
    gridTemplateColumns={{
      md: "repeat(2, 228px)",
      lg: "repeat(3, 228px)",
      xl: "repeat(4, 228px)",
    }}
    gridAutoColumns={{ xs: "minmax(228px, auto)" }}
    sx={(theme) => ({
      [theme.breakpoints.up("2xl")]: {
        gridTemplateColumns: "repeat(4, 266px)",
      },
    })}
  >
    {children}
  </Box>
))
PerkItems.displayName = "PerkItem"

/** A sub-component for the Perk Item individual item */
const PerkItem: FC<PerkItemProps> = ({ title, description, icon }) => {
  return (
    <Stack maxWidth={266}>
      <SvgIcon variant={icon as iconKeys} size={30} viewBox="0 0 30 30" sx={{ marginBottom: 7 }} />
      <Typography variant="heading.md" component="h2" mb={5}>
        {title}
      </Typography>
      <Typography variant="body.sm" dangerouslySetInnerHTML={{ __html: description }} />
    </Stack>
  )
}

/** A sub-component for the section container of the Perks List */
const PerksListSection: FCWC = ({ children }) => {
  return (
    <Box
      color={(theme) => theme.palette["secondary.800"].main}
      bgcolor={(theme) => theme.palette["secondary.200"].main}
    >
      <Container
        display="flex"
        flexDirection="column"
        gap={{ xs: 20, md: 40 }}
        maxWidth={1480}
        overflow="hidden"
        sx={(theme) => ({
          padding: { xs: theme.spacing(30, 7, 20), md: theme.spacing(55, 15) },
          marginTop: { xs: theme.spacing(15), md: theme.spacing(20) },
        })}
      >
        {children}
      </Container>
    </Box>
  )
}

/** A sub-component for the title of the Perks List */
const PerksListTitle: FC<Pick<PerksListProps, "title">> = ({ title }) => (
  <Typography
    component="h2"
    variant="heading.2xl"
    sx={(theme) => ({ color: theme.palette["secondary.800"].main })}
  >
    {title}
  </Typography>
)

/** A sub-component for the subtitle of the Perks List */
const PerksListSubtitle: FC<Pick<PerksListProps, "subtitle">> = ({ subtitle }) => (
  <Typography
    component="h3"
    variant="body.lg"
    sx={(theme) => ({ color: theme.palette["secondary.800"].main })}
  >
    {subtitle}
  </Typography>
)
